import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import ThresholdNote from './ThresholdNote';
import useMauDrilldown from './useMauDrilldown';
import RetryNotice from './RetryNotice';
import FlagSummary from './FlagSummary';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ row }) =>
  row.isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : row.threshold === null ? (
    <Typography variant="body2" color="text.disabled" align="center">-</Typography> // no threshold for the department: not judged
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const statusText = (row) => (row.isWarning ? 'Warning' : row.threshold === null ? 'Not configured' : 'OK');

// MAU = Monthly Active STUDENTS. The two cards are the company-wide numbers, for information. The THRESHOLD is per CRO and
// per department (MauConfig.jsx): every CRO's "Active students (at month end)" is compared with the number of their
// department, and a CRO handling fewer is flagged - so the by-CRO table is where the warnings are, worst first.
const MauDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const drill = useMauDrilldown(month); // a click on a CRO's student count opens the students behind it
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/mau-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load MAU');
          return;
        }
        setData(res?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load MAU');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  // Warnings first, the CRO furthest below the department's number at the top; CROs without a threshold last.
  const shortfall = (row) => (row.threshold === null ? -Infinity : row.threshold - row.activeAsOfPeriodEnd);
  const sortedRows = useMemo(() => [...(data?.rows || [])].sort((a, b) => shortfall(b) - shortfall(a)), [data]);

  const mauColumns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell row={row} />, csvValue: statusText },
    {
      field: 'entityName',
      headerName: 'CRO',
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'activatedThisPeriod', headerName: 'New students (first payment this month)', align: 'right', render: drill.cell('new', 'activatedThisPeriod') },
    { field: 'activeAsOfPeriodEnd', headerName: 'Active students (at month end)', align: 'right', render: drill.cell('active', 'activeAsOfPeriodEnd') },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      // the department's number: a CRO handling fewer active students than this is flagged
      render: (row) =>
        row.threshold === null ? (
          <Typography variant="body2" color="text.secondary">{row.thresholdOff ? 'Switched off' : 'Not configured'}</Typography>
        ) : (
          `< ${row.threshold} students`
        ),
      csvValue: (row) => (row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : `< ${row.threshold} students`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="MAU - Monthly Active Students"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('mau-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/mau-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && data && <ThresholdNote note={data.thresholdNote} month={month} configPath="mau-config" canSet={canConfigure('mau-config')} />}

      {!loading && !error && data && (
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Paper elevation={2} sx={{ p: 3, minWidth: 260 }}>
            <Typography variant="subtitle2" color="text.secondary">New Students This Period</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{data.activatedThisPeriod}</Typography>
            <Typography variant="caption" color="text.secondary">Students whose first payment was in this period (all CROs)</Typography>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, minWidth: 260 }}>
            <Typography variant="subtitle2" color="text.secondary">Active Students as of Period End</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{data.activeAsOfPeriodEnd}</Typography>
            <Typography variant="caption" color="text.secondary">Students active at the end of this period (all CROs). The threshold is checked per CRO, below.</Typography>
          </Paper>
        </Box>
      )}

      {!loading && !error && data && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">By CRO - the students each CRO is handling</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Only CROs with at least one active student. A CRO handling fewer active students than their department's threshold is flagged. Click a number to see the students.
          </Typography>
          <FlagSummary warningCount={data.warningCount || 0} totalEvaluated={data.totalEvaluated || 0} unit=" CROs" />
          <WarningMetricTable
            rows={sortedRows}
            columns={mauColumns}
            departmentName={departmentName}
            emptyMessage="No CRO is handling active students this period, in your scope."
            exportFileName="MAU_By_CRO"
          />
        </Box>
      )}
      {drill.dialog}
    </WarningPage>
  );
};

export default MauDashboard;
